/**
 * 用户管理 API
 */

import client from './client';

export type UserProfileSource = 'api_user' | 'credit_only' | 'install_only';

export interface User {
  device_id: string;
  support_id?: string | null;
  profile_source?: UserProfileSource;
  server_fingerprint: string | null;
  first_seen_at: string;
  last_seen_at: string;
  total_requests: number;
  app_version: string | null;
  device_model: string | null;
  system_version: string | null;
  country: string | null;
  last_ip: string | null;
  city: string | null;
  region: string | null;
  latitude?: string | null;
  longitude?: string | null;
  subscription_quota: number;
  subscription_type: string | null;
  is_blocked: boolean;
  blocked_until: string | null;
  custom_daily_limit: number | null;
  notes: string | null;
  current_day_requests?: number;
  effective_limit?: number;
  credit_available?: number;
  credit_held?: number;
  credit_anomaly?: boolean;
  anomaly_flags?: string[];
  seed_reported?: number | null;
  seed_applied?: number | null;
}


export interface CreditSnapshot {
  available: number;
  held: number;
  lifetime_granted: number;
  lifetime_spent: number;
  seeded: boolean;
  support_id?: string | null;
}

export interface CreditPreview {
  purchased: number;
  generate_count: number;
  reconstructed: number;
  client_quota: number;
}

export interface CreditSeedAudit {
  seed_reported: number | null;
  seed_applied: number | null;
  seed_purchase_sum: number | null;
  anomaly_flags: string[];
  anomaly_at: string | null;
  is_anomaly: boolean;
}

export interface CreditLedgerRow {
  id: number;
  entry_type: string;
  amount: number;
  available_after: number;
  held_after: number;
  source: string;
  reason?: string | null;
  transaction_id?: string | null;
  task_id?: string | null;
  created_at?: string;
}

export interface UserDetail extends User {
  monthly_stats: Array<{
    month_key: string;
    request_count: number;
  }>;
  recent_requests: Array<{
    endpoint: string;
    method: string;
    status_code: number | null;
    timestamp: string;
  }>;
  credits?: CreditSnapshot;
  credits_preview?: CreditPreview;
  credit_seed?: CreditSeedAudit;
  credit_ledger?: CreditLedgerRow[];
}

export interface UserListResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      page_size: number;
      total: number;
    };
  };
}

export interface UserDetailResponse {
  success: boolean;
  data: UserDetail;
}

export interface StatsOverview {
  total_users: number;
  active_users_today: number;
  active_users_this_month: number;
  total_requests_this_month: number;
  blocked_users: number;
  top_countries: Array<{
    country: string;
    count: number;
  }>;
  top_versions: Array<{
    version: string;
    count: number;
  }>;
}

export interface StatsResponse {
  success: boolean;
  data: StatsOverview;
}

/**
 * 获取用户列表
 */
export const listUsers = async (params?: {
  page?: number;
  page_size?: number;
  country?: string;
  is_blocked?: boolean;
  search?: string;
  credit_anomaly?: boolean;
}): Promise<UserListResponse> => {
  const query = {
    ...params,
    is_blocked:
      params?.is_blocked === undefined ? undefined : params.is_blocked ? 'true' : 'false',
    credit_anomaly:
      params?.credit_anomaly === undefined
        ? undefined
        : params.credit_anomaly
          ? 'true'
          : 'false',
  };
  const response = await client.get('/admin/users', { params: query });
  return response.data;
};

/**
 * 获取用户详情
 */
export const getUserDetail = async (device_id: string): Promise<UserDetailResponse> => {
  const response = await client.get(`/admin/users/${device_id}`);
  return response.data;
};

/**
 * 更新用户信息
 */
export const updateUser = async (
  device_id: string,
  data: {
    is_blocked?: boolean;
    blocked_until?: string | null;
    custom_daily_limit?: number | null;
    notes?: string | null;
  }
): Promise<{ success: boolean; message: string }> => {
  const response = await client.put(`/admin/users/${device_id}`, data);
  return response.data;
};

/**
 * 重置用户今日限额
 */
export const resetUserLimit = async (device_id: string): Promise<{ success: boolean; message: string }> => {
  const response = await client.post(`/admin/users/${device_id}/reset-limit`);
  return response.data;
};

export const adjustUserCredits = async (
  device_id: string,
  data: {
    amount: number;
    reason: string;
    idempotency_key: string;
  }
): Promise<{ success: boolean; message: string; credits: CreditSnapshot }> => {
  const response = await client.post(`/admin/users/${device_id}/credits/adjust`, data);
  return response.data;
};

/**
 * 获取统计总览
 */
export const getStatsOverview = async (): Promise<StatsResponse> => {
  const response = await client.get('/admin/stats/overview');
  return response.data;
};

/**
 * 购买事件
 */
export interface PurchaseEvent {
  id: number;
  device_id: string;
  transaction_id: string;
  product_id: string;
  product_type: string;
  purchase_date: string;
  expiration_date: string | null;
  quantity: number;
  price: number | null;
  currency: string | null;
}

export interface UserPurchasesResponse {
  success: boolean;
  data: {
    purchases: PurchaseEvent[];
  };
}

/**
 * 获取用户购买历史
 */
export const getUserPurchases = async (device_id: string): Promise<UserPurchasesResponse> => {
  const response = await client.get(`/admin/users/${device_id}/purchases`);
  return response.data;
};
